import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  orderBy,
  serverTimestamp,
  onSnapshot,
  writeBatch,
} from 'firebase/firestore';
import { getFirebaseDb } from './firebase';
import { handleAuthError } from './authRecoveryService';
import type {
  Account,
  CreateAccountInput,
  UpdateAccountInput,
} from '../types/settings';

// ============================================================================
// ACCOUNT CRUD
// ============================================================================

/**
 * Create a new account
 */
export async function createAccount(
  orgId: string,
  input: CreateAccountInput
): Promise<string> {
  const db = getFirebaseDb();
  const accountRef = doc(collection(db, 'organizations', orgId, 'accounts'));
  const accountId = accountRef.id;

  // If this is set as default, unset other defaults first
  if (input.isDefault) {
    await unsetAllDefaultAccounts(orgId);
  }

  await setDoc(accountRef, {
    id: accountId,
    entityId: input.entityId,
    accountName: input.accountName,
    accountNumber: input.accountNumber || null,
    type: input.type,
    isDefault: input.isDefault || false,
    isActive: true,
    createdAt: serverTimestamp(),
  });

  return accountId;
}

/**
 * Get all accounts for an organization
 */
export async function getAccounts(orgId: string): Promise<Account[]> {
  const db = getFirebaseDb();
  const accountsRef = collection(db, 'organizations', orgId, 'accounts');
  const q = query(accountsRef, orderBy('createdAt', 'asc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => doc.data() as Account);
}

/**
 * Get active accounts only
 */
export async function getActiveAccounts(orgId: string): Promise<Account[]> {
  const db = getFirebaseDb();
  const accountsRef = collection(db, 'organizations', orgId, 'accounts');
  const q = query(accountsRef, where('isActive', '==', true));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => doc.data() as Account);
}

/**
 * Subscribe to accounts (real-time updates)
 */
export function subscribeAccounts(
  orgId: string,
  callback: (accounts: Account[]) => void
): () => void {
  const db = getFirebaseDb();
  const accountsRef = collection(db, 'organizations', orgId, 'accounts');
  const q = query(accountsRef, orderBy('createdAt', 'asc'));

  return onSnapshot(
    q,
    (snapshot) => {
      const accounts = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Account[];
      callback(accounts);
    },
    async (error) => {
      console.error('[AccountService] Subscription error:', error);
      await handleAuthError(error);
    }
  );
}

/**
 * Update an account
 */
export async function updateAccount(
  orgId: string,
  accountId: string,
  input: UpdateAccountInput
): Promise<void> {
  const db = getFirebaseDb();
  await updateDoc(doc(db, 'organizations', orgId, 'accounts', accountId), {
    ...input,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Delete an account
 */
export async function deleteAccount(
  orgId: string,
  accountId: string
): Promise<void> {
  const db = getFirebaseDb();
  await deleteDoc(doc(db, 'organizations', orgId, 'accounts', accountId));
}

// ============================================================================
// DEFAULT ACCOUNT MANAGEMENT
// ============================================================================

/**
 * Helper to unset all default accounts
 */
async function unsetAllDefaultAccounts(orgId: string): Promise<void> {
  const db = getFirebaseDb();
  const accountsRef = collection(db, 'organizations', orgId, 'accounts');
  const q = query(accountsRef, where('isDefault', '==', true));
  const snapshot = await getDocs(q);

  const batch = writeBatch(db);
  snapshot.docs.forEach((docSnap) => {
    batch.update(docSnap.ref, { isDefault: false });
  });
  await batch.commit();
}

/**
 * Set an account as the default
 */
export async function setDefaultAccount(
  orgId: string,
  accountId: string
): Promise<void> {
  const db = getFirebaseDb();

  // First, unset all other defaults
  await unsetAllDefaultAccounts(orgId);

  // Then set this account as default
  await updateDoc(doc(db, 'organizations', orgId, 'accounts', accountId), {
    isDefault: true,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Get the default account
 */
export async function getDefaultAccount(orgId: string): Promise<Account | null> {
  const db = getFirebaseDb();
  const accountsRef = collection(db, 'organizations', orgId, 'accounts');
  const q = query(accountsRef, where('isDefault', '==', true));
  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  return snapshot.docs[0].data() as Account;
}

// ============================================================================
// ACCOUNT USAGE TRACKING
// ============================================================================

/**
 * Record that an account was used (updates lastUsed timestamp)
 */
export async function recordAccountUsage(
  orgId: string,
  accountId: string
): Promise<void> {
  const db = getFirebaseDb();
  await updateDoc(doc(db, 'organizations', orgId, 'accounts', accountId), {
    lastUsed: serverTimestamp(),
  });
}

/**
 * Get suggested accounts (sorted by lastUsed for auto-suggest)
 */
export async function getSuggestedAccounts(orgId: string): Promise<Account[]> {
  const accounts = await getActiveAccounts(orgId);

  // Sort by lastUsed (most recent first), then by default, then by accountName
  return accounts.sort((a, b) => {
    // Default account always first
    if (a.isDefault && !b.isDefault) return -1;
    if (!a.isDefault && b.isDefault) return 1;

    // Then by lastUsed
    if (a.lastUsed && b.lastUsed) {
      return b.lastUsed.toMillis() - a.lastUsed.toMillis();
    }
    if (a.lastUsed && !b.lastUsed) return -1;
    if (!a.lastUsed && b.lastUsed) return 1;

    // Finally by accountName
    return a.accountName.localeCompare(b.accountName);
  });
}

// ============================================================================
// ACCOUNT HELPERS
// ============================================================================

/**
 * Toggle account active status
 */
export async function toggleAccountActive(
  orgId: string,
  accountId: string,
  isActive: boolean
): Promise<void> {
  const db = getFirebaseDb();
  await updateDoc(doc(db, 'organizations', orgId, 'accounts', accountId), {
    isActive,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Get a specific account by ID
 */
export async function getAccount(
  orgId: string,
  accountId: string
): Promise<Account | null> {
  const db = getFirebaseDb();
  const accountDoc = await getDoc(
    doc(db, 'organizations', orgId, 'accounts', accountId)
  );

  if (!accountDoc.exists()) {
    return null;
  }

  return accountDoc.data() as Account;
}
